import { GoogleGenerativeAI } from '@google/generative-ai';
import { toast } from 'react-toastify';

class MCQService {
  constructor() {
    this.geminiClient = null;
    this.initializeService();
  }

  initializeService() {
    const geminiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (geminiKey) {
      try {
        this.geminiClient = new GoogleGenerativeAI(geminiKey);
        console.log('✅ MCQ Service with Gemini initialized');
      } catch (error) {
        console.error('❌ MCQ Service initialization failed:', error);
      }
    }
  }

  isAvailable() {
    return this.geminiClient !== null;
  }

  truncatePrompt(prompt, maxWords = 80) {
    if (!prompt) return '';
    const words = prompt.trim().split(/\s+/);
    if (words.length <= maxWords) return prompt;
    return words.slice(0, maxWords).join(' ') + '...';
  }

  async generateMCQs(concept, gradeLevel, language = 'English', count = 5) {
    if (!this.isAvailable()) {
      throw new Error('MCQ Service not available. Set REACT_APP_GEMINI_API_KEY');
    }

    try {
      toast.info('📝 Generating MCQs...', { autoClose: 3000 });

      const truncatedConcept = this.truncatePrompt(concept, 80);
      const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      const prompt = `Generate ${count} multiple choice questions about "${truncatedConcept}" for Grade ${gradeLevel} students in ${language}.

CRITICAL: Return ONLY valid JSON with no additional text, markdown, or explanations.

Return this exact JSON structure:
{
  "topic": "${truncatedConcept}",
  "grade": ${gradeLevel},
  "language": "${language}",
  "mcqs": [
    {
      "id": 1,
      "question": "Question text here?",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correct": 0,
      "explanation": "Brief explanation why this is correct"
    }
  ]
}

Requirements:
- Age-appropriate for Grade ${gradeLevel}
- Clear questions in ${language}
- 4 options per question
- One correct answer (index 0-3)
- Brief explanations
- Return ONLY valid JSON`;

      const response = await model.generateContent(prompt);
      let contentText = response.response.text().trim();

      contentText = contentText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      contentText = contentText.replace(/^[^{]*{/, '{');
      contentText = contentText.replace(/}[^}]*$/, '}');
      
      let parsedContent;
      try {
        parsedContent = JSON.parse(contentText);
        
        if (!parsedContent.mcqs || !Array.isArray(parsedContent.mcqs)) {
          throw new Error('Invalid MCQ structure');
        }
        
      } catch (jsonError) {
        console.warn('JSON parsing failed, creating fallback MCQs:', jsonError);
        
        parsedContent = {
          topic: truncatedConcept,
          grade: gradeLevel,
          language: language,
          mcqs: this.generateFallbackMCQs(truncatedConcept, count)
        };
      }

      const validatedMCQs = parsedContent.mcqs.map((mcq, index) => ({
        id: index + 1,
        question: mcq.question || `Question about ${truncatedConcept}?`,
        options: Array.isArray(mcq.options) && mcq.options.length >= 4 
          ? mcq.options.slice(0, 4) 
          : [`Correct answer about ${truncatedConcept}`, "Option B", "Option C", "Option D"],
        correct: typeof mcq.correct === 'number' && mcq.correct >= 0 && mcq.correct <= 3 
          ? mcq.correct 
          : 0,
        explanation: mcq.explanation || `This relates to ${truncatedConcept}`
      }));

      return {
        success: true,
        data: {
          topic: truncatedConcept,
          grade: gradeLevel,
          language: language,
          mcqs: validatedMCQs,
          totalQuestions: validatedMCQs.length,
          model: 'gemini-2.5-flash',
          source: 'MCQ Service',
          format: 'multiple_choice_questions'
        }
      };

    } catch (error) {
      console.error('MCQ generation failed:', error);
      
      return {
        success: true,
        data: {
          topic: concept,
          grade: gradeLevel,
          language: language,
          mcqs: this.generateFallbackMCQs(concept, count),
          totalQuestions: count,
          model: 'fallback',
          source: 'MCQ Service Fallback',
          format: 'multiple_choice_questions',
          note: 'Generated using fallback due to API issues'
        }
      };
    }
  }

  generateFallbackMCQs(concept, count) {
    const fallbackMCQs = [];
    for (let i = 1; i <= count; i++) {
      fallbackMCQs.push({
        id: i,
        question: `What is an important aspect of ${concept}?`,
        options: [
          `Key feature of ${concept}`,
          "Unrelated option A",
          "Unrelated option B", 
          "Unrelated option C"
        ],
        correct: 0,
        explanation: `This question tests understanding of ${concept}`
      });
    }
    return fallbackMCQs;
  }

  async generateMCQsFromImage(imageBase64, gradeLevel, language = 'English', count = 5) {
    if (!this.isAvailable()) {
      throw new Error('MCQ Service not available. Set REACT_APP_GEMINI_API_KEY');
    }

    try {
      toast.info('🖼️ Analyzing image and generating MCQs...', { autoClose: 4000 });

      const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      const prompt = `Analyze this educational image and generate ${count} multiple choice questions for Grade ${gradeLevel} students in ${language}.

CRITICAL: Return ONLY valid JSON with no additional text, markdown, or explanations.

Return this exact JSON structure:
{
  "imageDescription": "Brief description of what you see",
  "topic": "Main topic from the image",
  "grade": ${gradeLevel},
  "language": "${language}",
  "mcqs": [
    {
      "id": 1,
      "question": "Question about the image content?",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correct": 0,
      "explanation": "Brief explanation"
    }
  ]
}

Requirements:
- Base questions on image content
- Age-appropriate for Grade ${gradeLevel}
- Clear questions in ${language}
- 4 options per question
- Return ONLY valid JSON`;

      const imagePart = {
        inlineData: {
          data: imageBase64.replace(/^data:image\/[^;]+;base64,/, ''),
          mimeType: "image/jpeg"
        }
      };

      const response = await model.generateContent([prompt, imagePart]);
      let contentText = response.response.text().trim();

      contentText = contentText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      contentText = contentText.replace(/^[^{]*{/, '{');
      contentText = contentText.replace(/}[^}]*$/, '}');
      
      let parsedContent;
      try {
        parsedContent = JSON.parse(contentText);
      } catch (jsonError) {
        console.warn('Image MCQ JSON parsing failed, using fallback');
        parsedContent = {
          imageDescription: "Educational content from uploaded image",
          topic: "Image Analysis",
          grade: gradeLevel,
          language: language,
          mcqs: this.generateFallbackMCQs("Image Content", count)
        };
      }

      const validatedMCQs = (parsedContent.mcqs || []).map((mcq, index) => ({
        id: index + 1,
        question: mcq.question || "What do you see in this image?",
        options: Array.isArray(mcq.options) && mcq.options.length >= 4 
          ? mcq.options.slice(0, 4) 
          : ["Correct answer", "Option B", "Option C", "Option D"],
        correct: typeof mcq.correct === 'number' && mcq.correct >= 0 && mcq.correct <= 3 
          ? mcq.correct 
          : 0,
        explanation: mcq.explanation || "This relates to the image content"
      }));

      return {
        success: true,
        data: {
          imageDescription: parsedContent.imageDescription || "Educational image",
          topic: parsedContent.topic || "Image Analysis",
          grade: gradeLevel,
          language: language,
          mcqs: validatedMCQs,
          totalQuestions: validatedMCQs.length,
          model: 'gemini-2.5-flash',
          source: 'MCQ Service - Image Analysis',
          format: 'image_based_mcqs'
        }
      };

    } catch (error) {
      console.error('Image MCQ generation failed:', error);
      throw new Error(`Image MCQ generation failed: ${error.message}`);
    }
  }
}

export default MCQService;